<template>
  <base-slide-over ref="slideover" :title="isNew === true ? 'New Program' : 'Edit Program'" size="2xl" style="z-index: 10000">
    <base-form ref="programForm" class="tw-py-2.5 tw-px-4 sm:tw-py-4 sm:tw-px-6" v-model="isFormValid">
      <base-text-field label="Program Name" placeholder="Enter program name" v-model="businessProgram.name" class="tw-mt-3" required :maxLength="255" />
      <base-select label="Program Group" placeholder="Select a group" :options="programGroups" v-model="businessProgram.programGroup" />
      <base-text-field
        v-if="businessProgram.programGroup === 'new group'"
        label="New Program Group"
        placeholder="Enter new program group"
        v-model="businessProgram.newProgramGroup"
        :rules="[() => (this.businessProgram.programGroup === 'new group' && this.businessProgram.newProgramGroup !== null) || 'Please enter a new group name']"
        :maxLength="255"
        class="tw-mt-6"
      />
      <base-text-field
        label="Max Medications"
        placeholder="Enter the maximum medications for this program"
        v-model="businessProgram.maxMedications"
        required
        type="number"
        min="0"
        step="1"
        class="tw-mt-6"
      />
      <base-advanced-select
        class="tw-my-5"
        label="Allowed Medications"
        placeholder="Select allowed medications"
        :options="groupedMedicationOptions"
        v-model="businessProgram.allowedMedications"
        label-key="name"
        multiple
        :rules="[() => this.businessProgram.allowedMedications.length > 0 || 'Please choose at least one medication.']"
      />
    </base-form>
    <inline-error id="business-program-error" :title="errorTitle" :message="errorMessage" :error="error" />
    <template #fixed-bottom>
      <div class="tw-py-2 tw-px-3 tw-flex tw-justify-end">
        <base-button type="flat" class="tw-w-auto tw-mr-3" @click="close()"> Cancel </base-button>
        <base-button :loading="isSaving" :disabled="!isFormValid" class="tw-w-auto" @click="save"> Save </base-button>
      </div>
    </template>
  </base-slide-over>
</template>

<script>
import { mapGetters } from "vuex";
import InlineError from "@/components/InlineError";
import { orderBy } from "lodash";

export default {
  name: "BusinessProgramSlideover",
  props: {
    businessId: String,
  },
  components: { InlineError },
  data() {
    return {
      isFormValid: true,
      isSaving: false,
      isNew: false,
      businessProgram: {
        businessId: null,
        businessProgramId: null,
        name: null,
        maxMedications: null,
        programGroup: null,
        newProgramGroup: null,
        allowedMedications: [],
      },
      programGroups: [],
      medicationOptions: [],
      errorTitle: "",
      errorMessage: "",
      error: null,
    };
  },
  computed: {
    ...mapGetters("organization", ["organization"]),
    groupedMedicationOptions() {
      const options = [];
      const groups = [...new Set(this.medicationOptions.map((item) => item.productGroup))];

      groups.forEach((g) => {
        options.push({
          name: g,
          isHeader: true,
        });

        options.push(...orderBy(this.medicationOptions.filter((p) => p.productGroup === g), ["name"]));
      });

      return options;
    },
  },
  created() {
    this.getMedications();
  },
  methods: {
    reset() {
      this.isNew = false;
      this.businessProgram = {
        businessId: null,
        businessProgramId: null,
        name: null,
        maxMedications: null,
        programGroup: null,
        newProgramGroup: null,
        allowedMedications: [],
      };
      this.errorTitle = "";
      this.errorMessage = "";
      this.error = null;
    },
    show(businessId, businessProgram, programGroups) {
      this.reset();
      this.businessId = businessId;
      this.programGroups = programGroups;

      if (!businessProgram) this.isNew = true;

      if (businessProgram) {
        this.businessProgram.businessId = businessId;
        this.businessProgram.businessProgramId = businessProgram.businessProgramId;
        this.businessProgram.name = businessProgram.name;
        this.businessProgram.maxMedications = businessProgram.maxMedications;
        this.businessProgram.programGroup = businessProgram.programGroup;
        this.businessProgram.allowedMedications = businessProgram.allowedMedications;
      }

      this.$refs.slideover.show();
    },
    close() {
      this.$refs.slideover.hide();
    },
    getMedications() {
      const url = `/businesses/${this.businessId}/item-categories/all`;

      this.$axios.get(url).then((res) => {
        this.medicationOptions = res.data.filter((i) => i.type === "medication");
      });
    },
    save() {
      if (!this.$refs.programForm.validate()) return;
      if (this.businessProgram.newProgramGroup != null) this.businessProgram.programGroup = this.businessProgram.newProgramGroup;

      this.isSaving = true;

      if (this.isNew) {
        this.$axios
          .post(`/businesses/${this.businessId}/programs`, this.businessProgram)
          .then(() => {
            this.close();
            this.$emit("save");
            this.EventBus.$emit("GLOBAL_NOTIFY", { title: "Program Added", message: "Program " + this.businessProgram.name + " was created successfully" });
          })
          .catch((err) => {
            this.errorTitle = `Failed to create ${this.businessProgram.name}`;
            this.errorMessage = `Program ${this.businessProgram.name} was not able to be created. Please try again.`;
            this.error = err;
          })
          .finally(() => {
            this.isSaving = false;
          });
      } else {
        this.$axios
          .put(`/businesses/${this.businessId}/programs/${this.businessProgram.businessProgramId}`, this.businessProgram)
          .then(() => {
            this.close();
            this.$emit("save");
            this.EventBus.$emit("GLOBAL_NOTIFY", { title: "Program Updated", message: "Program " + this.businessProgram.name + " was updated successfully" });
          })
          .catch((err) => {
            this.errorTitle = `Failed to update ${this.businessProgram.name}`;
            this.errorMessage = `Program ${this.businessProgram.name} was not able to be updated. Please try again.`;
            this.error = err;
          })
          .finally(() => {
            this.isSaving = false;
          });
      }
    },
  },
};
</script>
